import React from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';
import IconButton from './IconButton';
import useDismissable from './useDismissable';
import './Drawer.css';

/*
 * Drawer — right slide-in panel with scrim, the mock's `.drawer`.
 *   open              : controls visibility (drives the slide transition)
 *   onClose(reason)   : 'escape' | 'scrim' | 'button'
 *   title, subtitle   : header content
 *   header            : full custom header (overrides title/subtitle)
 *   footer            : footer node (e.g. action buttons, right-aligned)
 *   width             : CSS width (default 'min(460px, 94vw)')
 *   side              : 'right' (default) | 'left'
 * Portals into <body>. Always mounted while `open` so the CSS transition runs;
 * when closed it returns null (no residual DOM).
 */
export default function Drawer({
  open,
  onClose,
  title,
  subtitle,
  header,
  footer,
  width,
  side = 'right',
  closeOnScrim = true,
  closeOnEsc = true,
  className = '',
  children,
  labelledBy,
  ...rest
}) {
  const panelRef = useDismissable(open, onClose, { closeOnEsc });
  if (!open) return null;

  const titleId = labelledBy || (title ? 'ui-drawer-title' : undefined);
  const onScrimClick = () => { if (closeOnScrim) onClose && onClose('scrim'); };

  return createPortal(
    <>
      <div className="ui-drawer-scrim open" onMouseDown={onScrimClick} aria-hidden="true" />
      <aside
        ref={panelRef}
        className={`ui-drawer ui-drawer--${side} open${className ? ' ' + className : ''}`}
        style={width ? { width } : undefined}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        {...rest}
      >
        <div className="ui-drawer__head">
          {header != null ? header : (
            <div className="grow">
              {title != null && <h2 id={titleId} className="h-sec">{title}</h2>}
              {subtitle != null && <div className="sub">{subtitle}</div>}
            </div>
          )}
          <IconButton label="Close" onClick={() => onClose && onClose('button')}>
            <Icon name="close" size={16} />
          </IconButton>
        </div>
        <div className="ui-drawer__body">{children}</div>
        {footer != null && <div className="ui-drawer__foot">{footer}</div>}
      </aside>
    </>,
    document.body
  );
}
